import axios from 'axios';
import { enqueue } from './offlineQueue';

// Default: relative `/api` so CRA's `proxy` config (localhost:3001) handles
// routing in normal local dev. Override with REACT_APP_API_BASE when the
// client needs to hit a specific absolute URL (e.g. LAN testing on iPad,
// where the iPad's browser can't use the PC-local proxy).
//   PowerShell example:
//     $env:REACT_APP_API_BASE="http://192.168.110.189:3001/api"; npm start
const API_BASE = process.env.REACT_APP_API_BASE || '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

// ── Auth token (JWT, localStorage-backed) ──
// Set after a magic-link round-trip. Attached as Authorization: Bearer
// on every outgoing request; server's requireAuth middleware verifies
// and resolves req.user. Legacy `cf.currentUserId` still set in
// parallel for any straggler client code that reads it directly —
// new code should use `currentUser` from the store, populated from
// /api/auth/me on boot.
const TOKEN_KEY = 'cf.authToken';
const USER_KEY  = 'cf.currentUserId';
export const getAuthToken    = () => localStorage.getItem(TOKEN_KEY) || '';
export const setAuthToken    = (t) => { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); };
export const getCurrentUserId = () => localStorage.getItem(USER_KEY) || '';
export const setCurrentUserId = (id) => {
  if (id) localStorage.setItem(USER_KEY, id);
  else    localStorage.removeItem(USER_KEY);
};

// ── Impersonation stash ──
// When an admin clicks "Log in as" in the admin console, we hold onto
// their original JWT here so a "Return to <admin>" banner can swap it
// back without forcing a re-login. Shape: { originalToken,
// originalName, targetName, startedAt }. Only ever populated via the
// admin-only POST /api/users/:id/impersonate flow.
const IMPERSONATOR_KEY = 'cf.impersonator';
export const getImpersonator = () => {
  try { return JSON.parse(localStorage.getItem(IMPERSONATOR_KEY) || 'null'); }
  catch (_) { return null; }
};
export const setImpersonator = (payload) => {
  if (payload) localStorage.setItem(IMPERSONATOR_KEY, JSON.stringify(payload));
  else         localStorage.removeItem(IMPERSONATOR_KEY);
};

// Append ?token=<jwt> (or &token=<jwt>) to a URL so authenticated GETs
// triggered directly by <img src>, <a href>, <iframe src>, or pdf.js
// pass auth. Those request paths bypass our axios interceptor, so the
// Authorization header isn't set; the server's requireAuth middleware
// accepts ?token= for exactly this escape hatch (see requireAuth.js).
export const withAuthToken = (url) => {
  const tok = getAuthToken();
  if (!tok) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(tok)}`;
};

api.interceptors.request.use((config) => {
  const tok = getAuthToken();
  if (tok) config.headers['Authorization'] = `Bearer ${tok}`;
  // Legacy header — still emitted so any code path I missed during
  // the auth migration keeps working. Server attaches a fresh value
  // from req.user anyway.
  const id = getCurrentUserId();
  if (id) config.headers['x-user-id'] = id;
  return config;
});

api.interceptors.response.use(
  (res) => res.data,
  async (err) => {
    // 401 → JWT missing or expired. Clear it so the next render of
    // App boots back into the SignInScreen instead of looping retries
    // against a dead token. Skip when the request was the auth-status
    // probe itself (/api/auth/me) so the boot flow can detect the
    // logged-out state without a side effect.
    if (err.response?.status === 401) {
      const url = err.config?.url || '';
      if (!url.includes('/auth/me')) {
        try {
          localStorage.removeItem('cf.authToken');
          localStorage.removeItem('cf.currentUserId');
          // Force a full re-render of the app so SignInScreen is shown.
          // Avoid in iframes / public-share viewer where reload would
          // disrupt context.
          if (typeof window !== 'undefined' && window.top === window) {
            window.location.reload();
          }
        } catch (_) {}
      }
    }

    // ── Offline mutation queuing ──────────────────────────────────────────
    // If there is no HTTP response (network failure) and the browser reports
    // offline, queue the mutation so it can be replayed on reconnect.
    const method = err.config?.method?.toLowerCase();
    const isMutation = ['post', 'patch', 'delete'].includes(method);
    const isNetworkError = !err.response && err.config;
    const isFormData =
      err.config?.data instanceof FormData ||
      err.config?.headers?.['Content-Type']?.includes('multipart');

    if (isNetworkError && !navigator.onLine && isMutation && !isFormData) {
      const { url, data } = err.config;
      const userId = localStorage.getItem('cf.currentUserId') || '';

      // Parse stringified body if axios serialized it
      let parsed;
      try { parsed = typeof data === 'string' ? JSON.parse(data) : data; }
      catch { parsed = data; }

      // Don't queue PATCH/DELETE aimed at a temp ID — the matching POST will
      // be re-applied on sync and will produce the same end state.
      const lastSegment = url.split('/').pop();
      if ((method === 'patch' || method === 'delete') && lastSegment?.startsWith('temp_')) {
        return method === 'delete' ? undefined : parsed;
      }

      // Assign a temporary client-side ID for new creates.
      const tempId =
        method === 'post'
          ? `temp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
          : null;

      await enqueue({ method, url, data: parsed, userId, tempId });

      // Return a synthetic response so the caller can continue normally.
      if (method === 'post') {
        return { ...(parsed ?? {}), id: tempId, _offline: true };
      }
      return { _offline: true };
    }
    // ─────────────────────────────────────────────────────────────────────

    const msg = err.response?.data?.error || err.message || 'Request failed';
    return Promise.reject(new Error(msg));
  }
);

// Auth (magic-link bootstrap → password sign-in)
export const requestMagicLink   = (email)  => api.post('/auth/request-link', { email });
export const passwordLogin      = (data)   => api.post('/auth/login', data);
export const setPassword        = (data)   => api.post('/auth/set-password', data);
export const fetchAuthMe        = ()       => api.get('/auth/me');
export const logout             = ()       => api.post('/auth/logout');

// Users
export const getUsers           = ()       => api.get('/users');
export const getMe              = ()       => api.get('/users/me');
export const createUser         = (data)   => api.post('/users', data);
// Admin-only: returns { token, user } so the caller can swap the JWT
// into their own storage and reload as the target user. See AdminUsersPanel.
export const impersonateUser    = (id)     => api.post(`/users/${id}/impersonate`);

// Project templates
export const getProjectTemplates= ()       => api.get('/project-templates');
export const getProjectTemplate = (id)     => api.get(`/project-templates/${id}`);

// Projects
export const getProjects        = ()       => api.get('/projects');
export const getProject         = (id)     => api.get(`/projects/${id}`);
export const createProject      = (data)   => api.post('/projects', data);
export const updateProject      = (id, d)  => api.patch(`/projects/${id}`, d);
export const deleteProject      = (id)     => api.delete(`/projects/${id}`);
export const getProjectMembers      = (id)         => api.get(`/projects/${id}/members`);
export const addProjectMember       = (id, d)      => api.post(`/projects/${id}/members`, d);
export const updateProjectMemberRole = (id, uid, role) =>
  api.patch(`/projects/${id}/members/${uid}`, { role });
export const removeProjectMember    = (id, uid)    => api.delete(`/projects/${id}/members/${uid}`);
export const seedProjectDisciplines = (id) => api.post(`/projects/${id}/seed-disciplines`);

// Disciplines
export const getDisciplines     = (pid)    => api.get('/disciplines', { params: { project_id: pid } });
export const createDiscipline   = (data)   => api.post('/disciplines', data);
export const updateDiscipline   = (id, d)  => api.patch(`/disciplines/${id}`, d);
export const deleteDiscipline   = (id)     => api.delete(`/disciplines/${id}`);
export const reorderDisciplines = (project_id, ids) => api.put('/disciplines/reorder', { project_id, ids });

// Version Sets
export const getVersionSets     = (pid)    => api.get('/version-sets', { params: { project_id: pid } });
export const createVersionSet   = (data)   => api.post('/version-sets', data);
export const updateVersionSet   = (id, d)  => api.patch(`/version-sets/${id}`, d);
export const deleteVersionSet   = (id, cascade = false) =>
  api.delete(`/version-sets/${id}`, { params: cascade ? { cascade_sheets: 'true' } : {} });
export const publishVersionSet   = (id) => api.post(`/version-sets/${id}/publish`);
export const unpublishVersionSet = (id) => api.post(`/version-sets/${id}/unpublish`);

// Sheets
export const getSheets          = (params) => api.get('/sheets', { params });
export const getDeletedSheets   = (params) => api.get('/sheets/deleted', { params });
export const getSheet           = (id)     => api.get(`/sheets/${id}`);
export const getSheetPdfUrl     = (id)     => withAuthToken(`${API_BASE}/sheets/${id}/pdf`);
export const getSheetThumbnailUrl = (id, w = 240) => withAuthToken(`${API_BASE}/sheets/${id}/thumbnail?w=${w}`);
export const uploadSheets       = (form)   => api.post('/sheets/upload', form, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 120000,
});
export const updateSheet        = (id, d)  => api.patch(`/sheets/${id}`, d);
export const bulkUpdateSheetTags = (sheet_ids, add, remove) =>
  api.post('/sheets/bulk-tags', { sheet_ids, add, remove });
export const setSheetCalibration = (id, cal) => api.put(`/sheets/${id}/calibration`, cal);
export const deleteSheet        = (id)     => api.delete(`/sheets/${id}`);
export const restoreSheet       = (id)     => api.post(`/sheets/${id}/restore`);
export const permanentDeleteSheet = (id)   => api.delete(`/sheets/${id}/permanent`);
export const permanentDeleteSheetsBulk = (sheet_ids) =>
  api.post('/sheets/permanent-delete-bulk', { sheet_ids });

// OCR (title-block extraction)
export const getOcrInfo         = (sheetId)          => api.get(`/ocr/info/${sheetId}`);
export const getOcrPageUrl      = (sheetId, page, scale = 0.6) =>
  withAuthToken(`${API_BASE}/ocr/page/${sheetId}/${page}?scale=${scale}`);
export const ocrCrop            = (sheetId, page, body) =>
  api.post(`/ocr/crop/${sheetId}/${page}`, body, { timeout: 120000 });
// Multi-region OCR: server loads + renders the page once and OCRs each region
// in turn. Halves round-trips and pdf.js doc loads vs sequential ocrCrop
// calls when the client wants several regions on the same sheet.
export const ocrCropMulti       = (sheetId, page, body) =>
  api.post(`/ocr/crop-multi/${sheetId}/${page}`, body, { timeout: 120000 });

// Markups
export const getMarkups         = (params) => api.get('/markups', { params });
export const createMarkup       = (data)   => api.post('/markups', data);
export const updateMarkup       = (id, d)  => api.patch(`/markups/${id}`, d);
export const deleteMarkup       = (id)     => api.delete(`/markups/${id}`);

// Folders — project_id-scoped tree of named folders (admin/pm mutate, all read)
export const getFolders         = (project_id) => api.get('/folders', { params: { project_id } });
export const createFolder       = (data)        => api.post('/folders', data);
export const updateFolder       = (id, data)    => api.patch(`/folders/${id}`, data);
export const deleteFolder       = (id)          => api.delete(`/folders/${id}`);

// Files — per-folder file attachments (admin/pm upload, all read/download)
export const getFiles           = (params) => api.get('/files', { params });
export const updateFile         = (id, d)  => api.patch(`/files/${id}`, d);
export const deleteFile         = (id)     => api.delete(`/files/${id}`);

// Multipart upload — FormData with `file`, `project_id`, optional `folder_id`
// and `description`. Axios needs the Content-Type to be left for the browser
// to set (so the multipart boundary is included).
export const uploadFile = ({ project_id, folder_id, description, file, onProgress }) => {
  const fd = new FormData();
  fd.append('project_id', project_id);
  if (folder_id) fd.append('folder_id', folder_id);
  if (description) fd.append('description', description);
  fd.append('file', file);
  return api.post('/files', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 15 * 60 * 1000,  // big files on slow links
    onUploadProgress: onProgress,
  });
};

// URL helpers — the browser handles the actual GET, so we just build a string.
// `dl=1` → Content-Disposition: attachment (force save). Otherwise inline so
// PDFs/images preview in a new tab.
export const getFileDownloadUrl = (id, { download } = {}) =>
  withAuthToken(`${API_BASE}/files/${id}/download${download ? '?dl=1' : ''}`);

// ── Forms ────────────────────────────────────────────────────────────────────
// Templates (auto-creates the daily_report template per project on first GET).
export const getFormTemplates       = (project_id)   => api.get('/form-templates', { params: { project_id } });
export const updateFormTemplate     = (id, d)        => api.patch(`/form-templates/${id}`, d);
export const deleteFormTemplate     = (id)           => api.delete(`/form-templates/${id}`);
export const getTemplatePermissions = (id)           => api.get(`/form-templates/${id}/permissions`);
export const setTemplatePermissions = (id, payload)  => api.put(`/form-templates/${id}/permissions`, payload);

// PDF template upload — multipart with file + project_id + name (+ description).
export const uploadFormTemplatePdf = ({ project_id, name, description, file, onProgress }) => {
  const fd = new FormData();
  fd.append('project_id', project_id);
  fd.append('name', name);
  if (description) fd.append('description', description);
  fd.append('file', file);
  return api.post('/form-templates', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 5 * 60 * 1000,
    onUploadProgress: onProgress,
  });
};

// Blank template PDF stream (used by the in-browser AcroForm renderer).
export const getFormTemplatePdfUrl = (id) => withAuthToken(`${API_BASE}/form-templates/${id}/pdf`);

// Filled PDF for a submission — computed on demand from template + data.
export const getFilledPdfUrl = (submission_id, { download } = {}) =>
  withAuthToken(`${API_BASE}/form-submissions/${submission_id}/filled-pdf${download ? '?dl=1' : ''}`);

// Submissions
export const getFormSubmissions     = (params)       => api.get('/form-submissions', { params });
export const getFormSubmission      = (id)           => api.get(`/form-submissions/${id}`);
export const createFormSubmission   = (data)         => api.post('/form-submissions', data);
export const updateFormSubmission   = (id, d)        => api.patch(`/form-submissions/${id}`, d);
export const deleteFormSubmission   = (id)           => api.delete(`/form-submissions/${id}`);

// Attachments — multipart POST to a submission. Same FormData pattern as
// uploadFile in the Files browser, but routed under the submission.
export const uploadFormAttachment = ({ submission_id, file, onProgress }) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/form-submissions/${submission_id}/attachments`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 15 * 60 * 1000,
    onUploadProgress: onProgress,
  });
};
export const deleteFormAttachment = (submission_id, attachment_id) =>
  api.delete(`/form-submissions/${submission_id}/attachments/${attachment_id}`);
export const getFormAttachmentUrl = (submission_id, attachment_id, { download } = {}) =>
  withAuthToken(`${API_BASE}/form-submissions/${submission_id}/attachments/${attachment_id}/download${download ? '?dl=1' : ''}`);

// Weather (Open-Meteo via /api/weather/project/:id?date=YYYY-MM-DD).
// Bumped timeout — geocoding via Nominatim can be slow for unfamiliar
// addresses on the first request.
export const getWeatherForProject = (project_id, date) =>
  api.get(`/weather/project/${project_id}`, { params: { date }, timeout: 60_000 });

// ── Drawings page overhaul ──────────────────────────────────────────────────
// Sheet activity log
export const getSheetEvents = (sheet_id) => api.get(`/sheets/${sheet_id}/events`);

// Saved searches (per user, per project)
export const getSavedSearches    = (project_id)         => api.get('/saved-searches', { params: { project_id } });
export const createSavedSearch   = (data)               => api.post('/saved-searches', data);
export const deleteSavedSearch   = (id)                 => api.delete(`/saved-searches/${id}`);

// Share links (admin/pm only). The public viewer fetches /api/share/:token
// directly; getPublicShareUrl is just the constructor for the URL we hand
// out to recipients.
export const listShareLinks      = (sheet_id)           => api.get('/share-links', { params: { sheet_id } });
export const createShareLink     = (data)               => api.post('/share-links', data);
export const revokeShareLink     = (id)                 => api.patch(`/share-links/${id}`, { revoked: true });
// Build the URL handed out to recipients. Uses the current origin so the
// link is shareable as long as the server is reachable from there.
export const getPublicShareUrl   = (token) => `${window.location.origin}/share/${token}`;
export const getPublicSharePdfUrl = (token) => `${API_BASE}/share/${token}/pdf`;
export const getPublicShareMeta  = (token) => api.get(`/share/${token}`);

// ── Concrete ─────────────────────────────────────────────────────────────
// Mix designs catalog. Read for any signed-in user, write for admin/PM.
export const getConcreteMixes    = (project_id) => api.get('/concrete-mixes', { params: { project_id } });
export const createConcreteMix   = (data)        => api.post('/concrete-mixes', data);
export const updateConcreteMix   = (id, d)       => api.patch(`/concrete-mixes/${id}`, d);
export const deleteConcreteMix   = (id)          => api.delete(`/concrete-mixes/${id}`);

// Used-in option list (project setting). Same role split.
// Rename via PATCH cascades into mixes + submissions on the server.
// Merge collapses one option into another, also cascading.
export const getUsedInOptions     = (project_id) => api.get('/concrete-used-in', { params: { project_id } });
export const getUsedInOptionUsage = (id)         => api.get(`/concrete-used-in/${id}/usage`);
export const createUsedInOption   = (data)        => api.post('/concrete-used-in', data);
export const updateUsedInOption   = (id, d)       => api.patch(`/concrete-used-in/${id}`, d);
export const mergeUsedInOption    = (from_id, to_id) =>
  api.post('/concrete-used-in/merge', { from_id, to_id });
export const deleteUsedInOption   = (id)          => api.delete(`/concrete-used-in/${id}`);

// Pour log derived from submitted daily reports. All filter params are
// optional except project_id; values match the server's accepted set.
export const getPourLog          = (params)      => api.get('/concrete-pour-log', { params });

export default api;
